import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  Alignment,
  AutoImage,
  BlockQuote,
  Bold,
  ClassicEditor,
  Code,
  CodeBlock,
  Essentials,
  FindAndReplace,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  Autosave,
  FontSize,
  Heading,
  Highlight,
  HorizontalLine,
  Image,
  ImageInsert,
  Italic,
  Link,
  List,
  MediaEmbed,
  Mention,
  Paragraph,
  RemoveFormat,
  SourceEditing,
  SpecialCharacters,
  Strikethrough,
  Subscript,
  Superscript,
  Table,
  Underline,
  Undo,
  Base64UploadAdapter,
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";
import styles from "./RichTextEditor.module.scss";
import { useEffect, useRef } from "react";

function RichTextEditor({
  value,
  onChange,
  autoSave,
  debounceDuration,
}: {
  value?: string;
  onChange?: (val: string) => void;
  autoSave?: (val: string) => void;
  debounceDuration?: number;
}) {
  const autoSaveRef = useRef(autoSave);
  useEffect(() => {
    autoSaveRef.current = autoSave;
  }, [autoSave]);

  return (
    <div className={"prose max-w-full h-full !bg-white"}>
      <CKEditor
        editor={ClassicEditor}
        data={value}
        onChange={(event, editor) => {
          const data = editor.getData();
          onChange?.(data);
        }}
        config={{
          plugins: [
            Essentials,
            Heading,
            Paragraph,
            Bold,
            Italic,
            Underline,
            Strikethrough,
            Subscript,
            Superscript,
            Link,
            List,
            BlockQuote,
            Table,
            Mention,
            Undo,
            Alignment,
            FontBackgroundColor,
            FontColor,
            FontSize,
            FontFamily,
            Highlight,
            HorizontalLine,
            Image,
            ImageInsert,
            AutoImage,
            MediaEmbed,
            RemoveFormat,
            SpecialCharacters,
            Code,
            CodeBlock,
            FindAndReplace,
            SourceEditing,
            Autosave,
            Base64UploadAdapter,
          ],

          autosave: {
            waitingTime: debounceDuration ?? 2000,
            save: async (editor) => {
              const data = editor.getData();
              try {
                autoSaveRef.current?.(data);
                console.log("Autosaved");
              } catch (err) {
                console.error("Autosave failed", err);
              }
            },
          },
          toolbar: [
            "heading", // Headings (H1, H2, H3, etc.)
            "|",
            "bold", // Bold text
            "italic", // Italic text
            "underline", // Underline text
            "strikethrough", // Strikethrough text
            "|",
            "subscript", // Subscript text
            "superscript", // Superscript text
            "link", // Hyperlinks
            "|",
            "bulletedList", // Bulleted list
            "numberedList", // Numbered list
            "blockQuote", // Blockquotes
            "|",
            "insertTable", // Tables
            "tableColumn", // Insert table column
            "tableRow", // Insert table row
            "mergeTableCells", // Merge table cells
            "|",
            "undo", // Undo action
            "redo", // Redo action
            "|",
            "alignment", // Text alignment (left, center, right, justify)
            "fontBackgroundColor", // Font background color
            "fontColor", // Font color
            "fontSize", // Font size
            "fontFamily", // Font family
            "highlight", // Text highlighting
            "horizontalLine", // Insert horizontal line
            "|",
            "imageUpload", // Image upload (requires additional setup for free image adapters)
            "mediaEmbed", // Embed media (videos, etc.)
            "|",
            "removeFormat", // Remove formatting
            "specialCharacters", // Insert special characters
            "|",
            "code", // Insert inline code
            "codeBlock", // Insert code block
            "|",
            "findAndReplace", // Find and replace text
            "sourceEditing", // Source code editing
          ],
          // Additional configuration options can be added here
        }}
      />
    </div>
  );
}

export default RichTextEditor;
